// src/components/ui/badge.tsx
import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

const badgeVariants = cva(
  'inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
  {
    variants: {
      variant: {
        default: 'border-transparent bg-primary text-primary-foreground hover:bg-primary/80',
        secondary: 'border-transparent bg-secondary text-secondary-foreground hover:bg-secondary/80',
        destructive: 'border-transparent bg-destructive text-destructive-foreground hover:bg-destructive/80',
        outline: 'text-foreground',
        // Priority variants
        critical: 'border-transparent bg-red-100 text-red-700',
        high: 'border-transparent bg-orange-100 text-orange-700',
        medium: 'border-transparent bg-yellow-100 text-yellow-700',
        low: 'border-transparent bg-green-100 text-green-700',
        // Status variants
        not_started: 'border-transparent bg-slate-100 text-slate-700',
        in_progress: 'border-transparent bg-blue-100 text-blue-700',
        completed: 'border-transparent bg-green-100 text-green-700',
        blocked: 'border-transparent bg-red-100 text-red-700',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  }
);

export interface BadgeProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof badgeVariants> {}

function Badge({ className, variant, ...props }: BadgeProps) {
  return <div className={cn(badgeVariants({ variant }), className)} {...props} />;
}

// Helper component for priority badges
interface PriorityBadgeProps {
  priority: string;
  className?: string;
}

function PriorityBadge({ priority, className }: PriorityBadgeProps) {
  const variant = priority.toLowerCase() as 'critical' | 'high' | 'medium' | 'low';
  return (
    <Badge variant={variant} className={className}>
      {priority}
    </Badge>
  );
}

// Helper component for status badges
interface StatusBadgeProps {
  status: string;
  className?: string;
}

function StatusBadge({ status, className }: StatusBadgeProps) {
  const variant = status as 'not_started' | 'in_progress' | 'completed' | 'blocked';
  const label = status.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
  return (
    <Badge variant={variant} className={className}>
      {label}
    </Badge>
  );
}

export { Badge, badgeVariants, PriorityBadge, StatusBadge };
